import React from 'react';

import * as sentry from '../../../utilities/sentry';
import markOnboardingCardAsComplete from './MarkOnboardingCardAsComplete.graphql';
import markOnboardingCardAsDismissed from './MarkOnboardingCardAsDismissed.graphql';
import {
  FULL_CAROUSEL_1_CARD_MAX_WIDTH_BREAKPT,
  FULL_CAROUSEL_1_CARD_SIZE,
  FULL_CAROUSEL_2_CARD_MAX_WIDTH_BREAKPT,
  FULL_CAROUSEL_2_CARD_SIZE,
  FULL_CAROUSEL_3_CARD_MAX_WIDTH_BREAKPT,
  FULL_CAROUSEL_3_CARD_SIZE,
  FULL_CAROUSEL_MAX_SIZE,
  mapCardKeytoComponent,
} from './constants';
import { getPersonalLibraryCardsConfig } from './ordered-card-sets/personal';
import { getSharedLibraryCardsConfig } from './ordered-card-sets/shared';
import { getTeamLibraryCardsConfig } from './ordered-card-sets/team';
import { showErrorBar } from '../../../actions/errorBar';
import { updateWorkspaceDetails } from '../../../actions/workspace';
import { ERROR } from '../../../constants/error-severities';
import { getGraphQLClient } from '../../../utilities/graphql';
import {
  PERSONAL_LIBRARY_INTRO_CARD,
  RECORD_VIDEO_CARD_KEY,
  SHARE_VIDEO_CARD_KEY,
  SHARED_LIBRARY_INTRO_CARD,
  TEAM_LIBRARY_INTRO_CARD,
  WORKSPACE_ELEMENT_COMPLETE,
  WORKSPACE_ELEMENT_DISMISSED,
} from '../../../../../shared/constants/workspaceOnboarding';
import {
  PRIVATE_WORKSPACE,
  SHARED_WORKSPACE,
  TEAM_WORKSPACE,
} from '../../../../../shared/constants/workspaces';

export const isCarouselAtEnd = (sliderRef, cards) => {
  const innerSliderRef = sliderRef?.current?.innerSlider;

  const innerSliderShown = innerSliderRef?.props.slidesToShow;
  const innerSliderSelected = innerSliderRef?.state.currentSlide;

  const lastIndex = innerSliderSelected + innerSliderShown;

  return lastIndex > cards.length - 1;
};

export const calculateFullCarouselWidthInPixels = (numCards, browserWidth) => {
  const getWidthbyNumCards = numCards => {
    if (numCards >= 4) {
      return FULL_CAROUSEL_MAX_SIZE;
    } else if (numCards === 3) {
      return FULL_CAROUSEL_3_CARD_SIZE;
    } else if (numCards === 2) {
      return FULL_CAROUSEL_2_CARD_SIZE;
    }

    return FULL_CAROUSEL_1_CARD_SIZE;
  };

  const getWidthByBrowserWidth = bWidth => {
    if (bWidth > FULL_CAROUSEL_3_CARD_MAX_WIDTH_BREAKPT) {
      return FULL_CAROUSEL_MAX_SIZE;
    } else if (bWidth > FULL_CAROUSEL_2_CARD_MAX_WIDTH_BREAKPT) {
      return FULL_CAROUSEL_3_CARD_SIZE;
    } else if (bWidth > FULL_CAROUSEL_1_CARD_MAX_WIDTH_BREAKPT) {
      return FULL_CAROUSEL_2_CARD_SIZE;
    }

    return FULL_CAROUSEL_1_CARD_SIZE;
  };

  const width = Math.min(
    getWidthbyNumCards(numCards),
    getWidthByBrowserWidth(browserWidth)
  );

  return `${width}px`;
};

const filterIneligibleCards = (onboardingCards, onboardingState) => {
  const _filterDismissedCards = card => {
    const [cardKey] = Object.keys(card);

    return card[cardKey]?.dismissable &&
      onboardingState[cardKey] === WORKSPACE_ELEMENT_DISMISSED
      ? null
      : cardKey;
  };

  const _applyCustomFilters = card => {
    const [cardValue] = Object.values(card);

    // if there is no filter, default to showing the card
    return cardValue?.filter ? cardValue.filter() : () => true;
  };

  const resultCards = [];

  onboardingCards.cards.forEach(cardItem => {
    const isCardArray = Array.isArray(cardItem);
    const item = isCardArray ? cardItem : [cardItem];

    if (resultCards.length > 1) {
      return null;
    }

    const filtered = item
      .filter(_filterDismissedCards)
      .filter(_applyCustomFilters);

    if (filtered.length > 0) {
      resultCards.push(filtered);
    }
  });

  return resultCards.flat();
};

const mapEligibleCardsToComponents = (onboardingState, onboardingCards) => {
  return filterIneligibleCards(onboardingCards, onboardingState).map(card => {
    const [cardKey] = typeof card === 'string' ? [card] : Object.keys(card);

    const CardElement = mapCardKeytoComponent[cardKey];

    return <CardElement key={cardKey} cardConfig={card[cardKey]} />;
  });
};

export const markEligibleElementAsCompleteForSelectedWorkspace = (
  dispatch,
  workspaces,
  cardKey,
  force = false
) => {
  const selectedWorkspace = workspaces?.find(workspace => workspace.isSelected);

  if (!selectedWorkspace) {
    return;
  }

  if (
    force ||
    canMarkElementAsComplete(selectedWorkspace.onboarding, cardKey)
  ) {
    completeElementHandler(dispatch, cardKey, selectedWorkspace.id);
  }
};

export const canMarkElementAsComplete = (onboarding, cardKey) => {
  const cardState = onboarding[cardKey];

  return ![WORKSPACE_ELEMENT_COMPLETE, WORKSPACE_ELEMENT_DISMISSED].includes(
    cardState
  );
};

export const dismissElementHandler = async (dispatch, cardKey, workspaceId) => {
  if (!cardKey || !dispatch) {
    return;
  }

  const gqlClient = getGraphQLClient();

  try {
    const { data } = await gqlClient.mutate({
      mutation: markOnboardingCardAsDismissed,
      variables: {
        cardKey,
        workspaceId,
      },
    });

    const { onboarding } = data;

    dispatch(
      updateWorkspaceDetails({
        id: workspaceId,
        onboarding,
      })
    );
  } catch (err) {
    dispatch(showErrorBar(ERROR, 'Oops! Failed to dismisss onboarding card.'));

    sentry.logException(err, 'Error dismissing workplace onboarding card');
  }
};

export const completeElementHandler = async (
  dispatch,
  cardKey,
  workspaceId
) => {
  if (!cardKey || !dispatch) {
    return;
  }

  const gqlClient = getGraphQLClient();

  try {
    const { data } = await gqlClient.mutate({
      mutation: markOnboardingCardAsComplete,
      variables: {
        cardKey,
        workspaceId,
      },
    });

    const { onboarding } = data;

    dispatch(
      updateWorkspaceDetails({
        id: workspaceId,
        onboarding,
      })
    );
  } catch (err) {
    dispatch(showErrorBar(ERROR, 'Oops! Failed to dismisss onboarding card.'));

    sentry.logException(err, 'Error dismissing workplace onboarding card');
  }
};

export const getPersonalLibraryCards = ({
  hasRecordingExperience,
  hasSharedVideo,
  isAdmin,
  userHasVideosInTeamLibrary,
  userCanCustomizeSharePage,
}) => {
  const onboardingCards = getPersonalLibraryCardsConfig({
    isAdmin,
    hasRecordingExperience,
    hasSharedVideo,
    userHasVideosInTeamLibrary,
    userCanCustomizeSharePage,
  });

  onboardingCards.cards = [
    PERSONAL_LIBRARY_INTRO_CARD,
    ...onboardingCards.cards,
  ];

  return onboardingCards;
};

export const getSharedLibraryCards = () => {
  const onboardingCards = getSharedLibraryCardsConfig({});

  onboardingCards.cards = [SHARED_LIBRARY_INTRO_CARD, ...onboardingCards.cards];

  return onboardingCards;
};

export const getTeamLibraryCards = ({
  isAdmin,
  userHasVideosInTeamLibrary,
  userCanCustomizeSharePage,
  hasSharedVideo,
}) => {
  const onboardingCards = getTeamLibraryCardsConfig({
    isAdmin,
    userCanCustomizeSharePage,
    userHasVideosInTeamLibrary,
    hasSharedVideo,
  });

  onboardingCards.cards = [TEAM_LIBRARY_INTRO_CARD, ...onboardingCards.cards];

  return onboardingCards;
};

export const isIntroCard = cardKey => {
  return [
    PERSONAL_LIBRARY_INTRO_CARD,
    SHARED_LIBRARY_INTRO_CARD,
    TEAM_LIBRARY_INTRO_CARD,
  ].includes(cardKey);
};

export const isAutoCompleteExcluded = (cardConfig, cardKey) => {
  return isIntroCard(cardKey) || !cardConfig?.dismissable;
};

export const cardHasBeenCompleted = (onboardingState, cardKey) => {
  return [WORKSPACE_ELEMENT_COMPLETE, WORKSPACE_ELEMENT_DISMISSED].includes(
    onboardingState[cardKey]
  );
};

export const cardHasBeenDismissed = (onboardingState, cardKey) => {
  return onboardingState[cardKey] === WORKSPACE_ELEMENT_DISMISSED;
};

export const fadeOutCard = (dispatch, card, fade, show, dismiss) => {
  // Fade and dismiss card after 3 seconds if it's complete
  setTimeout(() => {
    fade(true);

    // Let the fade animation play for half a second, then dismiss card
    setTimeout(() => {
      show(false);
      dismiss();

      if (card.onDismiss) {
        card.onDismiss(dispatch);
      }
    }, 500);
  }, 3000);

  if (card.onComplete) {
    card.onComplete(dispatch);
  }
};

export const getDashboardOnboardingCards = ({
  library,
  isAdmin,
  onboardingState,
  userCanCustomizeSharePage,
  teamLibraryVideos = [],
}) => {
  let onboardingCards = null;

  const hasRecordingExperience = cardHasBeenCompleted(
    onboardingState,
    RECORD_VIDEO_CARD_KEY
  );
  const hasSharedVideo = cardHasBeenDismissed(
    onboardingState,
    SHARE_VIDEO_CARD_KEY
  );

  const userHasVideosInTeamLibrary =
    teamLibraryVideos?.filter(v => v.currentUserIsOwner).length > 0;

  switch (library) {
    case TEAM_WORKSPACE:
      onboardingCards = getTeamLibraryCards({
        isAdmin,
        userCanCustomizeSharePage,
        userHasVideosInTeamLibrary,
        hasSharedVideo,
      });
      break;
    case SHARED_WORKSPACE:
      onboardingCards = getSharedLibraryCards();
      break;
    case PRIVATE_WORKSPACE:
      onboardingCards = getPersonalLibraryCards({
        isAdmin,
        hasSharedVideo,
        hasRecordingExperience,
        userHasVideosInTeamLibrary,
        userCanCustomizeSharePage,
      });
      break;
    default:
      onboardingCards = [];
  }

  return mapEligibleCardsToComponents(onboardingState, onboardingCards);
};
